import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Sparkles,
  Tv,
  ClipboardList,
  ShieldCheck,
  UserPlus,
  Crown,
  Zap,
  BarChart3,
  Smartphone,
  MessageCircle,
  Store,
} from "lucide-react";
import { DEMO_SHOP_SLUG } from "../lib/supabase";
import { listAllShops, getShopBySlug } from "../lib/api";
import { getSubdomainSlug } from "../lib/tenant";
import TawkChat from "../components/TawkChat";

export default function LandingPage() {
  const [shopCount, setShopCount] = useState(null);
  const tenantSlug = getSubdomainSlug();
  const [tenantShop, setTenantShop] = useState(null);
  const activeSlug = tenantSlug || DEMO_SHOP_SLUG;

  useEffect(() => {
    listAllShops()
      .then((s) => setShopCount(s?.length || 0))
      .catch(() => setShopCount(0));
  }, []);

  useEffect(() => {
    if (!tenantSlug) return;
    getShopBySlug(tenantSlug).then(setTenantShop).catch(() => {});
  }, [tenantSlug]);

  const heroKicker = tenantShop?.name
    ? `${tenantShop.name} · powered by AIBox`
    : "Smart Queue · Digital Customer Experience";

  return (
    <div className="min-h-screen bg-void text-white relative overflow-hidden">
      {/* Tawk.to live chat — Home/Sales only */}
      <TawkChat />
      {/* Decorative gradient orbs */}
      <div className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gold/10 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-gold/5 blur-[120px]" />

      <header className="relative z-10 px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="landing-brand">
          <div className="w-10 h-10 rounded-full bg-void border border-gold/40 flex items-center justify-center gold-glow">
            <Sparkles className="w-5 h-5 text-gold" strokeWidth={1.5} />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-lg">AIBox Global</div>
            <div className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
              Queue Platform
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <Link
            to="/super-admin"
            className="hidden md:inline-flex items-center gap-1.5 text-xs font-numbers tracking-[0.25em] uppercase text-white/50 hover:text-gold transition-colors"
            data-testid="landing-super-admin"
          >
            <Crown className="w-3.5 h-3.5" /> Super Admin
          </Link>
          <Link
            to="/diagnostics"
            className="hidden md:inline-flex items-center gap-1.5 text-xs font-numbers tracking-[0.25em] uppercase text-white/50 hover:text-gold transition-colors"
          >
            Diagnostics
          </Link>
        </div>
      </header>

      <main className="relative z-10 px-6 md:px-12 pt-8 md:pt-16 pb-24 max-w-6xl mx-auto">
        {/* HERO */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="font-numbers text-xs tracking-[0.4em] uppercase text-gold mb-6">
            {heroKicker}
          </div>
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
            The queue,
            <br />
            <em className="text-gold not-italic font-heading italic">reimagined</em>{" "}
            in gold.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/60 leading-relaxed">
            A premium white-label queue management platform for salons, clinics,
            banks and showrooms. Multi-tenant. Realtime. Mobile, iPad and Smart
            TV ready.
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link
              to="/signup"
              className="btn-gold inline-flex items-center gap-2"
              data-testid="landing-signup-cta"
            >
              Start Free <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to={`/s/${activeSlug}/book`}
              className="btn-ghost inline-flex items-center gap-2"
              data-testid="landing-book-cta"
            >
              {tenantSlug ? "Open Booking" : "Try Demo Booking"}
            </Link>
          </div>
        </motion.div>

        {/* DASHBOARD QUICK ACCESS */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-20"
        >
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="font-numbers text-xs tracking-[0.4em] uppercase text-gold mb-2">
                Quick Access · Dashboard
              </div>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight">
                Demo & test instantly
              </h2>
            </div>
            {shopCount !== null && (
              <div className="hidden md:block text-right">
                <div className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
                  Active Tenants
                </div>
                <div className="font-numbers text-3xl text-gold tabular-nums mt-1">
                  {shopCount}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <DashCard
              to={`/s/${activeSlug}/book`}
              testid="dash-add-customer"
              icon={UserPlus}
              kicker="01"
              title={tenantSlug ? "New Booking" : "Add Customer"}
              desc={
                tenantSlug
                  ? `Open ${tenantShop?.name || activeSlug} booking page.`
                  : "Book a token in 10 seconds on the demo shop."
              }
            />
            <DashCard
              to={`/s/${activeSlug}/tv`}
              testid="dash-tv"
              icon={Tv}
              kicker="02"
              title="Smart TV"
              desc="Open the live FIDS-style queue display."
            />
            <DashCard
              to={`/s/${activeSlug}/admin`}
              testid="dash-admin"
              icon={ShieldCheck}
              kicker="03"
              title="Shop Admin"
              desc={
                tenantSlug
                  ? `PIN-gated console for ${tenantShop?.name || activeSlug}.`
                  : "PIN 1234 — Call Next, Recall, Settings."
              }
            />
            <DashCard
              to="/super-admin"
              testid="dash-super-admin"
              icon={Crown}
              kicker="04"
              title="Super Admin"
              desc="Manage tenants, plans, payments, expiry."
            />
          </div>
        </motion.div>

        {/* FEATURES */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-20"
        >
          <div className="font-numbers text-xs tracking-[0.4em] uppercase text-gold mb-2">
            Capabilities
          </div>
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight">
            Built for premium hospitality
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            <FeatureRow icon={Zap} title="Realtime Updates" desc="Supabase channels + 5s polling fallback." />
            <FeatureRow icon={ClipboardList} title="Multi-Service Booking" desc="Stack services with live total time." />
            <FeatureRow icon={Tv} title="Smart TV with TTS" desc="Voice announcements + circular avatars." />
            <FeatureRow icon={MessageCircle} title="WhatsApp Notify" desc="Twilio/Gupshup/Generic webhook ready." />
            <FeatureRow icon={BarChart3} title="Daily Analytics" desc="Revenue, hourly flow, top services." />
            <FeatureRow icon={Smartphone} title="PWA Installable" desc="Add to Home Screen on iOS & Android." />
            <FeatureRow icon={Store} title="Self-Signup" desc="New tenants live in 60 seconds, no SQL." />
            <FeatureRow icon={Crown} title="Super Admin" desc="Plans, payments, expiry, suspension." />
            <FeatureRow icon={ShieldCheck} title="Suspension Gate" desc="Auto-locks expired/blocked shops." />
          </div>
        </motion.div>

        <div className="mt-20 hairline" />
        <div className="mt-8 text-xs font-numbers tracking-[0.25em] uppercase text-white/30">
          © {new Date().getFullYear()} AIBox Global · AIBoxHub.in · Premium White-Label SaaS
        </div>
      </main>
    </div>
  );
}

function DashCard({ to, icon: Icon, kicker, title, desc, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group block glass rounded-2xl p-6 hover:border-gold/40 transition-all hover:-translate-y-0.5"
    >
      <div className="flex items-start justify-between">
        <div className="font-numbers text-xs tracking-[0.3em] text-gold">{kicker}</div>
        <Icon className="w-5 h-5 text-white/70 group-hover:text-gold transition-colors" strokeWidth={1.5} />
      </div>
      <div className="font-heading text-xl mt-8">{title}</div>
      <div className="text-white/50 text-sm mt-2">{desc}</div>
    </Link>
  );
}

function FeatureRow({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-xl border border-white/10 p-5 hover:border-gold/30 transition-colors">
      <div className="flex items-center gap-2.5">
        <Icon className="w-4 h-4 text-gold" strokeWidth={1.5} />
        <div className="font-body text-base">{title}</div>
      </div>
      <div className="text-white/50 text-xs mt-2">{desc}</div>
    </div>
  );
}
